import { notification } from 'antd';
import { AxiosError } from 'axios';

export interface NotificationOptions {
  title?: string;
  description?: string;
  duration?: number;
  placement?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
}

class NotificationService {
  private defaultDuration = 4.5;
  private defaultPlacement: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' = 'topRight';

  // Success notification
  success(options: NotificationOptions | string) {
    if (typeof options === 'string') {
      notification.success({
        message: 'Success',
        description: options,
        duration: this.defaultDuration,
        placement: this.defaultPlacement,
      });
    } else {
      notification.success({
        message: options.title || 'Success',
        description: options.description,
        duration: options.duration || this.defaultDuration,
        placement: options.placement || this.defaultPlacement,
      });
    }
  }

  // Error notification
  error(options: NotificationOptions | string) {
    if (typeof options === 'string') {
      notification.error({
        message: 'Error',
        description: options,
        duration: this.defaultDuration,
        placement: this.defaultPlacement,
      });
    } else {
      notification.error({
        message: options.title || 'Error',
        description: options.description,
        duration: options.duration || this.defaultDuration,
        placement: options.placement || this.defaultPlacement,
      });
    }
  }

  // Warning notification
  warning(options: NotificationOptions | string) {
    if (typeof options === 'string') {
      notification.warning({
        message: 'Warning',
        description: options,
        duration: this.defaultDuration,
        placement: this.defaultPlacement,
      });
    } else {
      notification.warning({
        message: options.title || 'Warning',
        description: options.description,
        duration: options.duration || this.defaultDuration,
        placement: options.placement || this.defaultPlacement,
      });
    }
  }

  // Info notification
  info(options: NotificationOptions | string) {
    if (typeof options === 'string') {
      notification.info({
        message: 'Information',
        description: options,
        duration: this.defaultDuration,
        placement: this.defaultPlacement,
      });
    } else {
      notification.info({
        message: options.title || 'Information',
        description: options.description,
        duration: options.duration || this.defaultDuration,
        placement: options.placement || this.defaultPlacement,
      });
    }
  }

  // Handle API errors
  handleApiError(error: AxiosError | Error) {
    if (this.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      
      if (axiosError.response) {
        // Server responded with error status
        const status = axiosError.response.status;
        const data = axiosError.response.data as any;
        
        switch (status) {
          case 400:
            this.error({
              title: 'Bad Request',
              description: data.message || 'Invalid request data',
            });
            break;
            
          case 401:
            this.error({
              title: 'Unauthorized',
              description: 'Please log in to continue',
            });
            break;
            
          case 403:
            this.error({
              title: 'Forbidden',
              description: 'You don\'t have permission to perform this action',
            });
            break;
            
          case 404:
            this.error({
              title: 'Not Found',
              description: 'The requested resource was not found',
            });
            break;
            
          case 409:
            this.error({
              title: 'Conflict',
              description: data.message || 'Resource already exists',
            });
            break;
            
          case 422:
            this.error({
              title: 'Validation Error',
              description: data.message || 'Please check your input data',
            });
            break;
            
          case 429:
            this.warning({
              title: 'Rate Limit Exceeded',
              description: 'Too many requests. Please try again later',
            });
            break;
            
          case 500:
            this.error({
              title: 'Server Error',
              description: 'An internal server error occurred. Please try again later',
            });
            break;
            
          case 502:
          case 503:
          case 504:
            this.error({
              title: 'Service Unavailable',
              description: 'The service is temporarily unavailable. Please try again later',
            });
            break;
            
          default:
            this.error({
              title: `Error ${status}`,
              description: data.message || 'An unexpected error occurred',
            });
        }
      } else if (axiosError.request) {
        // Request was made but no response received
        this.error({
          title: 'Network Error',
          description: 'Unable to connect to the server. Please check your internet connection',
        });
      } else {
        // Something else happened
        this.error({
          title: 'Request Error',
          description: axiosError.message || 'An error occurred while making the request',
        });
      }
    } else {
      // Generic error
      this.error({
        title: 'Error',
        description: error.message || 'An unexpected error occurred',
      });
    }
  }

  // Handle validation errors
  handleValidationErrors(errors: string[]) {
    this.error({
      title: 'Validation Error',
      description: errors.join(', '),
      duration: 6, // Longer duration for validation errors
    });
  }

  // Close all notifications
  closeAll() {
    notification.destroy();
  }

  // Configure global settings
  configure(options: {
    duration?: number;
    placement?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
  }) {
    if (options.duration !== undefined) {
      this.defaultDuration = options.duration;
    }
    if (options.placement) {
      this.defaultPlacement = options.placement;
    }
    
    notification.config({
      duration: this.defaultDuration,
      placement: this.defaultPlacement,
    });
  }

  private isAxiosError(error: any): error is AxiosError {
    return error.isAxiosError === true;
  }
}

export const notificationService = new NotificationService();

// Export commonly used methods for convenience
export const showSuccess = (message: string | NotificationOptions) => 
  notificationService.success(message);

export const showError = (message: string | NotificationOptions) => 
  notificationService.error(message);

export const showWarning = (message: string | NotificationOptions) => 
  notificationService.warning(message);

export const showInfo = (message: string | NotificationOptions) => 
  notificationService.info(message);

export const handleApiError = (error: AxiosError | Error) => 
  notificationService.handleApiError(error);

export default notificationService;
